import React from 'react';
import styled from 'styled-components';
import { nationalRailImages } from '../Theming/images';
import { themeColor } from '../Theming';

import { usePageViewTracking, useQuery } from '../Common/Hooks';
import {
  ANALYTICS_ACTION_LABELS,
  ANALYTICS_PAGE_NAMES,
  PartnerNames,
  trackClickedActionEvent,
} from '../Common/Analytics/utils';
import { ContentPanel, ShoppingBag, Trolley } from './Components';
import { RegularText, Divider } from './Components/ContentPanel';

const Container = styled.div`
  position: relative;
  width: 100vw;
  height: 100vh;
  overflow: hidden;
  background-color: white;
  background-image: url(${nationalRailImages.nationalRailBackground});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;

  @media (max-height: 950px) {
    height: auto;
    min-height: 100vh;
    overflow-y: auto;
  }
`;

const Button = styled.button`
  background-color: ${themeColor('navy')};
  color: ${themeColor('gold')};
  border: none;
  padding: 1.2rem 2rem;
  font-size: 1.4rem;
  font-weight: 600;
  font-family: 'Century Gothic', sans-serif;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
  width: 100%;
  max-width: 400px;
  height: 61px;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const TextWithIcon = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const IconTextContent = styled.p`
  font-weight: 700;
  font-size: 24px;
  font-family: 'Century Gothic', sans-serif;
  margin: 0;
  color: ${themeColor('white')};
`;

const StyledRegularText = styled(RegularText)`
  font-size: 16px;
  font-family: 'Century Gothic', sans-serif;
  color: ${themeColor('white')};
  text-align: center;
  padding: 0 15px;
  margin: 0;
  font-weight: 400;
  line-height: normal;
  margin-top: 10px;
`;

const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0 20px;
  overflow: hidden;
`;

interface LandingProps {
  partnerName?: PartnerNames;
}

const Landing: React.FC<LandingProps> = ({ partnerName = 'NationalRail' }) => {
  const query = useQuery();
  const utm_campaign = query.get('utm_campaign') as PartnerNames;
  const effectivePartnerName = utm_campaign ?? partnerName;

  usePageViewTracking(
    ANALYTICS_PAGE_NAMES.desktop_activation_landing_page,
    effectivePartnerName
  );

  const onCTAClicked = () => {
    trackClickedActionEvent(
      query,
      effectivePartnerName,
      ANALYTICS_ACTION_LABELS.desktop_extension_install,
      ANALYTICS_PAGE_NAMES.desktop_activation_landing_page
    );
    window.location.href =
      'https://chromewebstore.google.com/detail/national-rail-ride-and-sh/eigmieiagagmmifedoeigoimedjdchlg?authuser=0&hl=en-GB';
  };

  return (
    <Container>
      <ContentPanel>
        <ContentContainer>
          <TextWithIcon>
            <ShoppingBag />
            <IconTextContent>Smart shopping on the go</IconTextContent>
          </TextWithIcon>
          <StyledRegularText>
            We find and verify deals, popping up at checkout to help you save
            money.
          </StyledRegularText>
          <Divider borderColorName="brightGreen" />
          <TextWithIcon>
            <Trolley />
            <IconTextContent>Ride and shop</IconTextContent>
          </TextWithIcon>
          <StyledRegularText>
            Save when you buy online with the best discount vouchers
          </StyledRegularText>
          <Divider borderColorName="magenta" />
          <Button id="letsGoButton" onClick={onCTAClicked}>
            Add Now
          </Button>
        </ContentContainer>
      </ContentPanel>
    </Container>
  );
};

export default Landing;
